import { selectApiErrors } from 'util/selector/apiSelector';

import { AxiosError } from 'axios';
import {
  FORM_VALIDATION_ERROR,
  UNKNOWN_ERROR,
  VALIDATION_ERROR,
} from 'exception/EmployeeCodeExpections';
import { Exception } from 'exception/Exception';
import { FormValidationError, SingleApiError, ValidationError } from 'exception/Exception.types';
import { FormValidationException } from 'exception/FormValidationException';

export const createUnknownException = (): Exception =>
  new Exception({
    code: UNKNOWN_ERROR,
    type: UNKNOWN_ERROR,
    message: 'Unknown error',
  });

const isApiError = (error: AxiosError): error is AxiosError<{ errors: SingleApiError[] }> => {
  const errors = (error?.response?.data as any)?.errors;
  return (
    Array.isArray(errors) &&
    errors.length > 0 &&
    errors.every((e: any) => typeof e.message === 'string' && typeof e.code === 'string')
  );
};

/**
 * Symfony validator return specific error structure
 */
const isFormValidationError = (
  error: AxiosError,
): error is AxiosError<{ errors: FormValidationError }> => {
  const errors = selectApiErrors<FormValidationError>(error);

  return (
    errors &&
    typeof errors.message === 'string' &&
    errors.type === FORM_VALIDATION_ERROR &&
    errors.violations.every((e) => typeof e.message === 'string' && typeof e.path === 'string')
  );
};

const areFormValidationErrors = (error: AxiosError): error is AxiosError<ValidationError[]> => {
  const errors = selectApiErrors<ValidationError[]>(error);

  return (
    Array.isArray(errors) &&
    errors.length > 0 &&
    errors.every(
      (err) =>
        typeof err.message === 'string' &&
        err.code === VALIDATION_ERROR &&
        typeof err.path === 'string',
    )
  );
};

const isStringArrayError = (error: AxiosError): boolean => {
  const errors = (error?.response?.data as any)?.errors;
  return (
    Array.isArray(errors) && errors.length > 0 && errors.every((e: any) => typeof e === 'string')
  );
};

export const createException = (error: AxiosError): Exception => {
  if (isFormValidationError(error)) {
    const { message, violations } = selectApiErrors<FormValidationError>(error);
    return new FormValidationException(message, violations);
  }

  if (areFormValidationErrors(error)) {
    const errors = selectApiErrors<ValidationError[]>(error);
    return new Exception({
      type: VALIDATION_ERROR,
      additionalFields: errors,
      code: undefined,
      message: errors?.[0]?.message,
    });
  }

  if (isApiError(error)) {
    const apiErrors = selectApiErrors(error);

    if (apiErrors?.length) {
      const { message, code, ...additionalFields } = apiErrors[0];
      return createApiException(message, code, additionalFields);
    }
  }

  if (isStringArrayError(error)) {
    const errors = (error?.response?.data as any)?.errors;
    return createApiException(errors[0], undefined, undefined);
  }

  return createUnknownException();
};

const createApiException = (
  message: string | undefined,
  code: string | undefined,
  additionalFields: any,
) => {
  return new Exception({
    type: 'API_ERROR',
    message: message ?? 'Unknown error occurred.',
    code: code ?? 'UNKNOWN_ERROR',
    additionalFields,
  });
};
